import React, { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'

import { FormControl, MenuItem, Select } from '@mui/material'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Stepper from '@mui/material/Stepper'
import { ChevronLeftRounded } from '@mui/icons-material'
import { getSnapshot, Instance } from 'mobx-state-tree'

import { AssignmentHelper, useStores, ISelectorOption } from '@trivie/core'
import { Text } from '@trivie/ui-web/components/text'
import { Button } from '@trivie/ui-web/components/button'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { AssignmentApi } from '@trivie/core/src/services/api-objects/AssignmentApi'
import {
  AssignmentCreate,
  AssignmentEdit,
} from '@trivie/core/src/models/response-models/assignment'
import { color } from '@trivie/ui-web/theme/color'
import { AssignmentCreateForm } from '@trivie/core/src/models/AssignmentCreateForm'

import { AssignmentAssignees } from './assignment-assignees'
import { AssignmentSchedule } from './assignment-schedule'
import { AssignmentReview } from './assignment-review'
import { AutocompleteSelector } from '../../../../components/autocomplete-selector'

type AssignmentCreateDialogProps = {
  open: boolean
  initialTopics?: Array<number>
  initialQuizzes?: Array<number>
  initialPaths?: Array<number>
  editingId?: number
  requestClose: () => void
  fromContentCreation?: boolean
  fromTopics?: boolean
}

export const AssignmentCreateDialog = observer((props: AssignmentCreateDialogProps) => {
  const { assignmentStore, manageUserStore } = useStores()
  const { userProfile } = manageUserStore
  const { open, initialTopics, initialQuizzes, initialPaths, editingId, requestClose, fromTopics } =
    props
  const [activeStep, setActiveStep] = useState(0)
  const [form, setForm] = useState<Instance<typeof AssignmentCreateForm> | null>(null)
  const steps = ['Content', 'Assignees', 'Schedule', 'Review']
  const [isLoading, setIsLoading] = useState(false)
  const [editContentIds, setEditContentIds] = useState<{ id: number; type: string }[]>()

  const [assignmentType, setAssignmentType] = useState<any>(
    initialPaths?.length ? 'Learning Paths' : initialQuizzes?.length ? 'Quizzes' : 'Topics',
  )

  const handleAddItem = (selected: ISelectorOption[]) => {
    form?.set(
      'content',
      selected.map((item) => ({
        id: item.id,
        type: item.type,
        label: item.label,
        subLabel: item.subLabel ?? '',
        photo_url: item.photo_url ?? undefined,
        parentId: item.parentId ?? undefined,
        childCount: item.childCount ?? undefined,
        checked: true,
        subItems:
          item.subItems?.map((si) => ({
            id: si.id,
            type: si.type,
            label: si.label,
            subLabel: si.subLabel ?? '',
            parentId: si.parentId ?? undefined,
            checked: false,
          })) ?? null,
      })),
    )
  }

  // Build preselect IDs from initial props or editing data
  const getPreselectIds = () => {
    const ids: { id: number; type: string }[] = []
    if (initialTopics?.length) {
      initialTopics.forEach((id) => ids.push({ id, type: 'topics' }))
    }
    if (initialQuizzes?.length) {
      initialQuizzes.forEach((id) => ids.push({ id, type: 'quizzes' }))
    }
    if (initialPaths?.length) {
      initialPaths.forEach((id) => ids.push({ id, type: 'learning paths' }))
    }
    return ids.length > 0 ? ids : undefined
  }

  useEffect(() => {
    if (open) {
      if (editingId) {
        setIsLoading(true)
        AssignmentApi.getAssignmentEditData(editingId)
          .then((assignmentEditData) => {
            if (assignmentEditData.ok && assignmentEditData.data) {
              const loadedForm = AssignmentHelper.createAssignmentCreateForm({
                start_date: assignmentEditData.data.start_date
                  ? DateTime.fromISO(assignmentEditData.data.start_date).toFormat('yyyy-MM-dd')
                  : DateTime.local().toFormat('yyyy-MM-dd'),
                start_time: assignmentEditData.data.start_date
                  ? DateTime.fromISO(assignmentEditData.data.start_date).toLocaleString(
                      DateTime.TIME_24_SIMPLE,
                    )
                  : DateTime.local().toLocaleString(DateTime.TIME_24_SIMPLE),
                end_date: assignmentEditData.data.end_date
                  ? DateTime.fromISO(assignmentEditData.data.end_date).toFormat('yyyy-MM-dd')
                  : DateTime.local().plus({ months: 1 }).toFormat('yyyy-MM-dd'),
                endDateEnabled: Boolean(assignmentEditData.data.end_date),
                end_time: assignmentEditData.data.end_date
                  ? DateTime.fromISO(assignmentEditData.data.end_date).toLocaleString(
                      DateTime.TIME_24_SIMPLE,
                    )
                  : DateTime.local().toLocaleString(DateTime.TIME_24_SIMPLE),
                title: assignmentEditData.data.title ?? '',
                content: undefined,
                groups:
                  assignmentEditData.data.group_ids?.map((g: any) => ({
                    group_id: g,
                    label: '',
                    subLabel: '',
                    checked: true,
                    subItems: null,
                    count: 0,
                  })) ?? [],
                users:
                  assignmentEditData.data.user_ids?.map((u: any) => ({
                    id: u,
                    label: '',
                    subLabel: '',
                    checked: true,
                    subItems: null,
                    count: 0,
                  })) ?? [],
                attempts: assignmentEditData.data.max_attempts ?? 5,
                attemptsEnabled: assignmentEditData.data.max_attempts !== 999999,
                daysToComplete: assignmentEditData.data.days_to_complete ?? 7,
                daysToCompleteEnabled: Boolean(assignmentEditData.data.days_to_complete),
                notify: assignmentEditData.data.notify ?? false,
                include_pre_tests: assignmentEditData.data.include_pre_tests ?? false,
                include_post_tests: assignmentEditData.data.include_post_tests ?? false,
              })

              const contentIds: { id: number; type: string }[] = []
              if (assignmentEditData?.data?.topics?.length > 0) {
                setAssignmentType('Topics')
                assignmentEditData.data.topics.forEach((id: number) =>
                  contentIds.push({ id, type: 'topics' }),
                )
              } else if (assignmentEditData?.data?.quizzes?.length > 0) {
                setAssignmentType('Quizzes')
                assignmentEditData.data.quizzes.forEach((id: number) =>
                  contentIds.push({ id, type: 'quizzes' }),
                )
              } else if (assignmentEditData?.data?.paths?.length > 0) {
                setAssignmentType('Learning Paths')
                assignmentEditData.data.paths.forEach((id: number) =>
                  contentIds.push({ id, type: 'learning paths' }),
                )
              }
              if (contentIds.length > 0) {
                setEditContentIds(contentIds)
              }

              setForm(loadedForm)
            }
          })
          .finally(() => setIsLoading(false))
      } else {
        const formDefaults = AssignmentHelper.createAssignmentCreateForm({})
        setForm(formDefaults)
      }
    }
  }, [open])

  if (!form) {
    return null
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <div
              style={{
                flexDirection: 'row',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 32,
              }}
            >
              <div style={{ flexDirection: 'column', display: 'flex' }}>
                <Text text="Assignment Type" variant="body2Medium" />
                <Text
                  text={
                    userProfile.workspace_settings.learning_paths_enabled
                      ? 'Assign learning paths, topics or quizzes'
                      : 'Assign topics or quizzes'
                  }
                  variant="body2"
                  style={{ color: color.shade50 }}
                />
              </div>
              <FormControl>
                <Select
                  disabled={Boolean(fromTopics || initialQuizzes?.length || initialPaths?.length)}
                  style={{ width: 207, height: 41 }}
                  value={assignmentType}
                  onChange={(e) => {
                    form.set('content', [])
                    setAssignmentType(e.target.value)
                  }}
                >
                  <MenuItem value="Topics">Topics</MenuItem>
                  <MenuItem value="Quizzes">Quizzes</MenuItem>
                  {userProfile.workspace_settings.learning_paths_enabled && (
                    <MenuItem value="Learning Paths">Learning Paths</MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>
            <AutocompleteSelector
              key={assignmentType}
              variant={
                assignmentType === 'Topics'
                  ? 'topics'
                  : assignmentType === 'Quizzes'
                    ? 'quizzes'
                    : 'learning paths'
              }
              open
              noOptionsText="No content."
              preselectIds={getPreselectIds() ?? editContentIds}
              defaultOptions={
                form.content.length > 0
                  ? (getSnapshot(form.content) as unknown as ISelectorOption[])
                  : undefined
              }
              onSelectionChange={handleAddItem}
              style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
            />
          </>
        )
      case 1:
        return <AssignmentAssignees editingId={editingId} form={form} />
      case 2:
        return <AssignmentSchedule form={form} />
      case 3:
        return <AssignmentReview form={form} />

      default:
        return 'Unknown step'
    }
  }

  const isLastStep = () => {
    return activeStep > steps.length
  }

  const handleNext = async () => {
    if (activeStep + 1 === 2 && initialPaths?.length) {
      if (form.selectedPaths?.length) {
        form.set('title', `${form.selectedPaths[0].label} Assignment`)
      }
      setActiveStep(2)
    } else if (activeStep + 1 === 4) {
      const due_date = form.daysToCompleteEnabled
        ? DateTime.fromJSDate(new Date(`${form.start_date}T${form.start_time}:00`))
            .plus({ days: form.daysToComplete || 0 })
            .toUTC()
            .toISO()
        : null
      const end_date = DateTime.fromJSDate(new Date(`${form.end_date}T${form.end_time}:00`))
        .toUTC()
        .toISO()

      if (editingId) {
        const editPayload = {
          title: form.title,
          max_attempts: form.attemptsEnabled ? form.attempts || 5 : 999999,
          is_test: false,
          notify: form.notify,
          topics: form.selectedTopics.filter((f) => f.type === 'topics').map((t) => t.id),
          content_ordering: [],
          lessons: [],
          quizzes: form.selectedQuizzes.map((q) => q.id),
          group_ids: form.groups.map((g) => g.id),
          user_ids: form.users.map((u) => u.id),
          include_pre_tests: form.include_pre_tests,
          include_post_tests: form.include_post_tests,
          days_to_complete: form.daysToCompleteEnabled ? form.daysToComplete : null,
          start_date: DateTime.fromJSDate(new Date(`${form.start_date}T${form.start_time}:00`))
            .toUTC()
            .toISO(),
          end_date: form.endDateEnabled ? end_date : null,
          due_date,
          is_archived: false,
          is_deleted: false,
          paths: form.selectedPaths.map((path) => path.id),
        }
        AssignmentApi.patchAssignment(editingId, AssignmentEdit.create(editPayload))
      } else {
        assignmentStore.setTriggerRefresh(true)
        const payload = AssignmentCreate.create({
          title: form.title,
          max_attempts: form.attemptsEnabled ? form.attempts || 5 : 999999,
          is_test: false,
          notify: form.notify,
          topics: form.selectedTopics.filter((f) => f.type === 'topics').map((t) => t.id),
          content_ordering: [],
          lessons: [],
          quizzes: form.selectedQuizzes.map((q) => q.id),
          group_ids: form.groups.map((g) => g.id),
          user_ids: form.users.map((u) => u.id),
          include_pre_tests: form.include_pre_tests,
          include_post_tests: form.include_post_tests,
          days_to_complete: form.daysToCompleteEnabled ? form.daysToComplete : null,
          start_date: DateTime.fromJSDate(new Date(`${form.start_date}T${form.start_time}:00`))
            .toUTC()
            .toISO(),
          end_date: form.endDateEnabled ? end_date : null,
          due_date,
          paths: form.selectedPaths.map((path) => path.id),
        })
        AssignmentApi.createAssignment(payload)
      }

      setActiveStep(4)
    } else if (activeStep + 1 === 5) {
      setActiveStep(0)
      requestClose()
    } else {
      const newActiveStep = !isLastStep() ? activeStep + 1 : activeStep
      setActiveStep(newActiveStep)
    }
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const stepIsValid = () => {
    if (activeStep === 0) {
      return Boolean(
        form.selectedQuizzes.length || form.selectedTopics.length || form.selectedPaths.length,
      )
    } else if (activeStep === 1) {
      return Boolean(form.groups?.length > 0 || form.users?.length > 0)
    } else if (activeStep === 2) {
      return !Object.keys(form.errors).length
    } else {
      return true
    }
  }

  const getButtonText = () => {
    if (editingId && activeStep === 2) {
      return 'Review'
    } else if (editingId && activeStep === 3) {
      return 'Save'
    } else if (editingId) {
      return 'Next'
    } else if (activeStep === 3) {
      return 'Assign'
    } else {
      return 'Next'
    }
  }

  return (
    <div>
      <Dialog
        open={activeStep === 4}
        title={editingId ? 'Edit Assignment' : 'Create Assignment'}
        onConfirm={handleNext}
        dialogText={
          editingId
            ? 'Assignment Updated!'
            : `The assignment "${form.title}" was successfully created!`
        }
        onClose={handleNext}
        maxWidth="xs"
        confirmBtnLabel="Close"
      />
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="md"
        onClose={() => {
          setActiveStep(0)
          requestClose()
        }}
        sx={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        title={editingId ? 'Edit Assignment' : 'Create Assignment'}
        open={open && activeStep !== 4}
        actions={
          <div
            style={{
              display: 'flex',
              flex: 1,
              justifyContent: activeStep === 0 ? 'flex-end' : 'space-between',
            }}
          >
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                startIcon={<ChevronLeftRounded />}
                text="Back"
                variant="text"
              />
            )}
            <Button onClick={handleNext} disabled={!stepIsValid()} text={getButtonText()} />
          </div>
        }
      >
        <Stepper
          style={{ paddingLeft: 0, paddingRight: 0, marginBottom: 32 }}
          nonLinear
          activeStep={activeStep}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepButton disableRipple disabled>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            transition: 'all 0.5s ease-in-out',
            height: 500,
          }}
        >
          {getStepContent(activeStep)}
        </div>
      </Dialog>
    </div>
  )
})
